import classNames from "classnames";
import { onAuthStateChanged } from "firebase/auth";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import io from "socket.io-client";
import { Call } from "./components/Calls/Call";
import { notifyCallCreated } from "./utils/callEvents";
import GuardedRoute from "./components/GuardedRoute";
import { Toast } from "./components/Toast";
import "./assets/scss/style.scss";

import {
  AgencyPath,
  ApplicationPublicFormPath,
  AutomationPath,
  BackupAgentsPath,
  CampaignPath,
  ClientPublicFormPath,
  ClientsPath,
  CommissionsPath,
  ConversationsPath,
  DashboardPath,
  DocumentsPath,
  ForgotPasswordPath,
  LandingPath,
  LeadsPath,
  LeadsPoolPath,
  LoginPath,
  MetadataPath,
  NewAutomationPath,
  NewClientPath,
  NewLeadPath,
  NewUserPath,
  PhonePath,
  PoliciesPath,
  PolicySellsReportPath,
  ProfilePath,
  TagsPath,
  UpdateAutomationPath,
  UpdateClientPath,
  UpdateLeadPath,
  UpdateUserPath,
  UsersPath,
} from "./constants/Routes";

import { IncomingMessage } from "./models/TwilioConversation";
import { AgencyDetails } from "./pages/Agency/AgencyDetails";
import { ApplicationPublicForm } from "./pages/ApplicationPublicForm";
import { Automations } from "./pages/Automations/Automations";
import NewAutomation from "./pages/Automations/NewAutomation";
import UpdateAutomation from "./pages/Automations/UpdateAutomation";
import { ClientPublicForm } from "./pages/ClientPublicForm";
import { Clients } from "./pages/Clients/Clients";
import { Commissions } from "./pages/Commissions/Commissions";
import { Conversations } from "./pages/Conversations/Conversations";
import { Phone } from "./pages/Conversations/Phone";
import { Dashboard } from "./pages/Dashboard/DashBoard";
import { Documents } from "./pages/Documents/Documents";
import { Leads } from "./pages/Leads/Leads";
import { ForgotPassword } from "./pages/Login/ForgotPassword";
import { Login } from "./pages/Login/Login";
import { Metadata } from "./pages/Metadata/Metadata";
import { Policies } from "./pages/Policies/Policies";
import { Profile } from "./pages/Profile/Profile";
import { Tags } from "./pages/Tags/Tags";
import { Users } from "./pages/Users/Users";
import { PolicySellsReport } from "./pages/Reports/PolicySellsReport";
import {
  fetchAgencyBillingPeriods,
  setCurrentAgency,
} from "./state/agencies/action";
import { fetchFollowUps } from "./state/automations/action";
import { fetchClients } from "./state/clients/actions";
import { fetchLeads, fetchLeadsPool } from "./state/leads/actions";
import { fetchMetadataMenuItems } from "./state/metadata/action";
import { fetchPolicies } from "./state/policies/actions";
import { AppDispatch, RootState } from "./state/store";
import { fetchTags } from "./state/tags/action";
import { fetchDocumentTemplates } from "./state/templates/actions";
import {
  fetchCalls,
  fetchConversations,
  handleIncomingMessage,
  health,
} from "./state/twilio/actions";
import {
  fetchAgencyAgents,
  fetchAgentsTwilioNumbers,
  fetchBackUpOfUsers,
  fetchMyBackUpUsers,
  fetchUser,
  fetchUsers,
  fetchUserTwilioPhones,
  removeUser,
} from "./state/users/actions";
import { getLocalTimeZone, isLoggedIn } from "./utils/functions";
import Landing from "./pages/Landing/Landing";
import { Home } from "./pages/Home/Home";
import { fetchAgencyDocusignConfig } from "./state/docusignConfig/actions";
import { Campaigns } from "./pages/Campaigns/Campaigns";
import { fetchCampaigns } from "./state/campaigns/actions";
import { ClientForm } from "./pages/Clients/ClientForm";
import { LeadsPool } from "./pages/Leads/LeadsPool";
import {
  fetchAgencyPolicyTypes,
  fetchAllPolicyTypes,
} from "./state/policyMarkets/actions";
import moment from "moment";
import {
  fetchUserReminders,
  removeUserNotificationReminder,
  setUserNotificationReminder,
  setUserTodayReminders,
} from "./state/reminders/action";
import { fetchAgencyCarriers } from "./state/carriers/actions";
import { fetchAgencyMailTypes } from "./state/mail/actions";
import { RoleEnum } from "./enums/Role";
import { clearCalls, TwilioState } from "./state/twilio/slice";
import { UserForm } from "./pages/Users/UserForm";
import { LeadForm } from "./pages/Leads/LeadForm";
import { getAuth, getAnalytics, firebaseApp } from "./firebase/firebaseConfig";

export const App = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const twilio:TwilioState = useSelector((state: any) => state.twilio);
  const users = useSelector((state: any) => state.users);
  const dashboard = useSelector((state: any) => state.dashboard);
  const clients = useSelector((state: any) => state.clients);
  const leads = useSelector((state: any) => state.leads);
  const [isUserSignedIn, setIsUserSignedIn] = useState(isLoggedIn);
  const [errorOnCurrentUser, setErrorOnCurrentUser] = useState(false);
  const [showPreloader, setShowPreloader] = useState(true);
  const location = useLocation();
  const phoneContact = useSelector((state: any) => state.twilio.phoneContact);
  const reminders = useSelector(
    (state: RootState) => state.reminders.reminders
  );
  const todayReminders = useSelector(
    (state: RootState) => state.reminders.todayReminders
  );
  const [page, setPage] = useState(1);

  // Usamos useRef para mantener el ID del setTimeout
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (errorOnCurrentUser) {
      //FirebaseService.logout();
      Toast(
        t(users?.error?.reason ?? "SOMETHING_WENT_WRONG"),
        t(users?.error?.cause_info)
      );
      setErrorOnCurrentUser(false);
    }
  }, [errorOnCurrentUser, users?.error, t]);

  useEffect(() => {
    // Solicitud de acceso para abrir micrófono al abrir la web
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop()); // Liberar el micrófono por ahora
      })
      .catch((err) => {
        // Agregar en caso de error al solicitar permisos de microfono
      });
  }, []);

  useEffect(() => {
    async function load() {
      if (users?.currentUser) {
        const timeZone = getLocalTimeZone();
        let start;

        const startDate = moment().hours(0).minutes(0).seconds(0).toDate();
        start = moment(startDate).format("YYYY-MM-DD");

        await Promise.all([
          dispatch(fetchUsers(users?.currentUser.agency_id)),
          users?.currentUser.role_id === RoleEnum.AGENT ? dispatch(fetchAgencyAgents(users?.currentUser.agency_id)) : null,
          dispatch(fetchAllPolicyTypes()),
          dispatch(fetchAgencyCarriers(users?.currentUser.agency_id)),
          dispatch(fetchAgencyPolicyTypes(users?.currentUser.agency_id)),
          dispatch(fetchAgencyMailTypes(users?.currentUser.agency_id)),
          dispatch(fetchCampaigns({ page: 0, pageSize: 10, filters: {} })),
          dispatch(fetchUserReminders({ startDate: start, timeZone: timeZone })),
          dispatch(fetchUserTwilioPhones()),
          dispatch(fetchFollowUps()),
          //dispatch(fetchAutomations()),
          dispatch(fetchTags(users?.currentUser.agency_id)),
          dispatch(
            fetchClients({
              page: 0,
              pageSize: 10,
              filters:
                users?.currentUser.role_id === RoleEnum.AGENT
                  ? { agents: [users?.currentUser.id] }
                  : {},
              changeClients: true,
            })
          ),
          dispatch(fetchPolicies({ page: 0, pageSize: 10 })),
          dispatch(
            fetchLeads({
              page: 0,
              pageSize: 10,
              filters:
                users?.currentUser.role_id === RoleEnum.AGENT
                  ? { agents: [users?.currentUser.id] }
                  : {},
              changeLeads: true,
            })
          ),
          dispatch(fetchLeadsPool({ page: 0, pageSize: 10, filters: {} })),
          dispatch(health()),
          users?.currentUser.role_id === RoleEnum.AGENT
            ? dispatch(fetchMyBackUpUsers())
            : null,
          users?.currentUser.role_id === RoleEnum.AGENT
            ? dispatch(fetchBackUpOfUsers())
            : null,
          dispatch(fetchDocumentTemplates()),
          dispatch(fetchTags(users?.currentUser.agency_id)),
          dispatch(setCurrentAgency(users?.currentUser.agency)),
          dispatch(fetchMetadataMenuItems()),
          dispatch(fetchAgentsTwilioNumbers()),
          dispatch(fetchAgencyBillingPeriods(users?.currentUser.agency_id)),
          dispatch(fetchAgencyDocusignConfig(users?.currentUser.agency_id)),
        ]);
      }
    }
    load();
  }, [users?.currentUser]);

  const mainTwilioNumber = useMemo(
    () => users?.currentUser?.main_twilio_number?.twilio_phone,
    [users?.currentUser?.main_twilio_number?.twilio_phone]
  );

  useEffect(() => {
    async function loadToken() {
      if (mainTwilioNumber) {
        dispatch(clearCalls());

        //await dispatch(fetchVoiceToken(mainTwilioNumber));
        await dispatch(fetchConversations());
        await dispatch(fetchCalls({}))
      }
    }
    loadToken();
  }, [mainTwilioNumber, page]);

  useEffect(() => {
    if (users?.currentUser) {
      const socket = io(process.env.REACT_APP_SOCKET_URL ?? "");
      //const socket = io("ws://localhost:8080" ?? "");
      socket.emit("registry", {
        userId: users?.currentUser.id,
        phone: mainTwilioNumber,
      });
      socket.on("connect", () => {});
      socket.on("registry", (data) => {});
      socket.on("disconnect", () => {});
      socket.on("incommingMessage", (message: IncomingMessage) => {
        dispatch(handleIncomingMessage(message));
      });
      return () => {
        socket.disconnect();
      };
    }
  }, [users?.currentUser, mainTwilioNumber, dispatch]);

  useEffect(() => {
    return () => {
      if (twilio.device) {
        twilio.device.destroy();
      }
    };
  }, [twilio.device]);

  // Función que revisa los recordatorios
  const checkTodayReminders = () => {
    const todayHour = moment(new Date());

    todayReminders.forEach((reminder) => {
      const reminderHour = moment(reminder.date);
      const diff = reminderHour.diff(todayHour, "minutes"); // Diferencia en minutos

      // Si el recordatorio está dentro de 10 minutos de la hora actual
      if (diff <= 10 && reminderHour.isSameOrAfter(todayHour)) {
        dispatch(setUserNotificationReminder(reminder));
      } else {
        if (reminderHour.isBefore(todayHour)) {
          dispatch(removeUserNotificationReminder(reminder));
        }
      }
    });

    // Configurar el siguiente chequeo para el próximo minuto
    timeoutRef.current = setTimeout(checkTodayReminders, 60000); // Verifica nuevamente después de 1 minuto
  };

  useEffect(() => {
    checkTodayReminders();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [todayReminders, dispatch]);

  useEffect(() => {
    if (reminders.length > 0) {
      const todayDate = moment()
        .hours(0)
        .minutes(0)
        .seconds(0)
        .format("YYYY-MM-DD");
      const todayR = reminders
        .filter((r) => {
          const rDate = moment(r.date).format("YYYY-MM-DD");
          return moment(rDate).isSame(todayDate);
        })
        .sort((a, b) => {
          if (
            moment(a.date).isBefore(new Date()) &&
            moment(b.date).isBefore(new Date())
          ) {
            if (moment(a.date).isBefore(b.date)) {
              return -1;
            } else if (moment(a.date).isAfter(b.date)) {
              return 1;
            }
            return 0;
          } else if (
            moment(a.date).isBefore(new Date()) &&
            moment(b.date).isAfter(new Date())
          ) {
            return 1;
          } else if (
            moment(a.date).isAfter(new Date()) &&
            moment(b.date).isBefore(new Date())
          ) {
            return -1;
          } else {
            if (moment(a.date).isBefore(b.date)) {
              return -1;
            } else if (moment(a.date).isAfter(b.date)) {
              return 1;
            }
            return 0;
          }
        });
      dispatch(setUserTodayReminders(todayR));
    }
  }, [reminders]);


  const makePhoneCall = useCallback(
    (phone: string) => {
      if (twilio.device) {
        const params = { To: phone };
        twilio.device.connect({params})
        .then((outgoingCall: any) => {
          notifyCallCreated(outgoingCall, 'App.tsx - makePhoneCall');
        }).catch((error: any) => {
          console.error('Failed to create outgoing call:', error);
        });
      }
    },
    [twilio.device]
  );

  useEffect(() => {
    if (
      location.pathname.startsWith("/register/") ||
      location.pathname === "/"
    ) {
      setShowPreloader(false);
    } else if (
      location.pathname !== ForgotPasswordPath &&
      location.pathname !== LoginPath &&
      clients &&
      twilio &&
      users &&
      leads &&
      dashboard &&
      users.currentUser?.id &&
      !clients.isLoading &&
      !twilio.isLoading &&
      !users.isLoading &&
      !leads.isLoading &&
      !dashboard.isLoading
    ) {
      setShowPreloader(false);
    }
  }, [clients, twilio, users, leads, dashboard, location]);

  useLayoutEffect(() => {
    const analytics = getAnalytics(firebaseApp);
    const auth = getAuth(firebaseApp);
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        dispatch(fetchUser()).then((e) => {
          if (e.type === "users/currentUser/rejected") {
            setErrorOnCurrentUser(true);
          }
        });
        setIsUserSignedIn(true);
      } else {
        //Remove user from local storage
        dispatch(removeUser());
        setIsUserSignedIn(false);
        setShowPreloader(false);
      }
    });
  }, []);

  return (
    <>
      <div
        className={classNames({
          "bg-blue flex items-center justify-center h-screen fixed top-0 left-0 right-0 bottom-0 z-[100]":
            true,
          block: showPreloader,
          preloader: !showPreloader,
          //hidden: location.pathname === LoginPath ||location.pathname === ForgotPasswordPath,
        })}
      >
        <div className="flex justify-center items-center h-screen">
          <svg
            className="loading-triangle -mx-1.5"
            width="48.12"
            height="46"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polygon points="0,10.6 24.06,46 48.12,10.6" fill="white" />
          </svg>
          <svg
            className="loading-triangle -mx-1.5"
            width="48.12"
            height="46"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polygon points="48.12,46 24.06,10.6 0,46" fill="white" />
          </svg>
          <svg
            className="loading-triangle -mx-1.5"
            width="48.12"
            height="46"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polygon points="0,10.6 24.06,46 48.12,10.6" fill="white" />
          </svg>
        </div>
      </div>
      <div id="content">
        
        <Call/>
        <Routes>
          {/* Rutas públicas accesibles para todos */}
          <Route path={ClientPublicFormPath} element={<ClientPublicForm />} />
          <Route path={LandingPath} element={<Landing />} />
          <Route path={ApplicationPublicFormPath} element={<ApplicationPublicForm />} />

          {/* Rutas no autenticadas */}
          <Route element={<GuardedRoute isRouteAccessible={!isUserSignedIn} redirectRoute={DashboardPath}/>}>
            <Route path={LoginPath} element={<Login />} />
            <Route path={ForgotPasswordPath} element={<ForgotPassword />} />
          </Route>

          {/* Rutas autenticadas */}
          <Route element={<GuardedRoute isRouteAccessible={isUserSignedIn} redirectRoute={LoginPath} />}>
            <Route element={<Home />}>
              <Route path={DashboardPath} element={<Dashboard />} />
              <Route path={ClientsPath} element={<Clients makePhoneCall={makePhoneCall} />} />
              <Route path={NewClientPath} element={<ClientForm />} />
              <Route path={PoliciesPath} element={<Policies />} />
              <Route path={ConversationsPath} element={
                <Conversations makePhoneCall={makePhoneCall} />
              }/>
              <Route
                path={PhonePath}
                element={
                  <Phone
                    makePhoneCall={makePhoneCall}
                  />
                }
              />
              <Route path={LeadsPath} element={<Leads makePhoneCall={makePhoneCall} />} />
              <Route path={NewLeadPath} element={<LeadForm />} />
              <Route path={DocumentsPath} element={<Documents />} />
              <Route path={UsersPath} element={<Users />} />
              <Route path={BackupAgentsPath} element={<Users />} />
              <Route path={NewUserPath} element={<UserForm />} />
              <Route path={ProfilePath} element={<Profile />} />
              <Route path={AgencyPath} element={<AgencyDetails />} />
              <Route path={UpdateClientPath} element={<ClientForm />} />
              <Route path={UpdateLeadPath} element={<LeadForm />} />
              <Route path={LeadsPoolPath} element={<LeadsPool makePhoneCall={makePhoneCall} />} />
              <Route path={UpdateUserPath} element={<UserForm />} />
              <Route path={AutomationPath} element={<Automations />} />
              <Route path={NewAutomationPath} element={<NewAutomation />} />
              <Route path={UpdateAutomationPath} element={<UpdateAutomation />} />
              <Route path={TagsPath} element={<Tags />} />
              <Route path={MetadataPath} element={<Metadata />} />
              <Route path={CommissionsPath} element={<Commissions />} />
              <Route path={CampaignPath} element={<Campaigns />} />
            </Route>
            {/*<Route path={SalesPolicyLeadBoardPath} element={<SalesPolicyLeadBoard />} />*/}
            <Route path={PolicySellsReportPath} element={<PolicySellsReport />} />
          </Route>

          {/* Redirección global para rutas no encontradas */}
          <Route path="*" element={<Navigate to={isUserSignedIn ? DashboardPath : LoginPath} />} />
        </Routes>
      </div>
    </>
  );
};
